import { useLazyOrderShowPaymentSlipQuery } from '@entities/orders/deposit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Tooltip } from '@mui/material';
import { useModal } from '@shared/components/Modal';

import './view-slip.css';

type EntityType = 'order' | 'request';

type IProps = {
  entityId: string;
  entityType: EntityType;
  disabled?: boolean;
};

export const ViewSlip = ({ entityId, entityType, disabled }: IProps) => {
  const { openModal } = useModal();
  const [fetchOrderSlip, { isLoading: isOrderLoading }] = useLazyOrderShowPaymentSlipQuery();

  const isLoading = isOrderLoading;

  const handleViewSlip = async () => {
    try {
      let result;

      switch (entityType) {
        case 'order':
          result = await fetchOrderSlip(entityId).unwrap();
          break;
        case 'request':
          result = await fetchOrderSlip(entityId).unwrap();
          break;
        default:
          throw new Error(`Unsupported entity type: ${entityType}`);
      }

      const slipPath = result.slip_path || result.slip_url || null;

      if (!slipPath) {
        const noSlipContent = (
          <div className='view-slip-modal-content'>
            <h3 className='view-slip-title'>
              Slip for {entityType} #{entityId}
            </h3>
            <div className='view-slip-error-message'>No slip available for this {entityType}.</div>
          </div>
        );
        openModal(noSlipContent, 'lg');
        return;
      }

      const slipContent = (
        <div className='view-slip-modal-content'>
          <h3 className='view-slip-title'>
            Slip for {entityType} #{entityId}
          </h3>
          <div className='view-slip-image-container'>
            <img
              src={slipPath}
              alt={`Slip for ${entityType} ${entityId}`}
              className='view-slip-image'
              onError={e => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `<p class="view-slip-error-message">Unable to load slip image.<br/>Path: ${slipPath}</p>`;
                }
              }}
            />
          </div>
        </div>
      );

      openModal(slipContent, 'lg');
    } catch (error) {
      const errorContent = (
        <div className='view-slip-modal-content'>
          <h3 className='view-slip-title'>
            Slip for {entityType} #{entityId}
          </h3>
          <div className='view-slip-error-message'>
            Unable to load slip.
            <br />
            Error: {error instanceof Error ? error.message : 'Slip not found'}
          </div>
        </div>
      );

      openModal(errorContent, 'lg');
    }
  };

  if (isLoading) {
    return (
      <Tooltip title='Loading slip...'>
        <ReceiptIcon sx={{ color: '#ffa726', cursor: 'wait' }} />
      </Tooltip>
    );
  }

  if (disabled) {
    return (
      <Tooltip title='Slip not available'>
        <ReceiptIcon sx={{ color: '#939393', cursor: 'pointer' }} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title='View slip'>
      <ReceiptIcon sx={{ color: '#1976d2', cursor: 'pointer' }} onClick={handleViewSlip} />
    </Tooltip>
  );
};
